import { createApiRef, DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';

/** @public */
export interface DevOpsMetricsData {
  id?: string;
  platformCode?: string;
  portfolio?: string;
  beatId?: string;
  beatName?: string;
  ghaName?: string;
  ghaEnvironment?: string;
  repoUrl?: string;
  date?: string;
  deploymentFrequency?: number;
  leadTime?: number;
  mttr?: number;
  changeFailureRate?: number;
  buildSuccess?: number;
  testCoverage?: number;
  vulnerabilities?: number;
  [key: string]: any;
}

/** @public */
export interface DevOpsMetricsResponse {
  data: DevOpsMetricsData[];
  totalRecords: number;
  repository?: string;
  dateRange: {
    from: string;
    to: string;
  };
}

/** @public */
export interface DevOpsMetricsQueryParams {
  entityRef?: string;
  dateFrom?: string;
  dateTo?: string;
  platformCode?: string;
  portfolio?: string;
  beatId?: string;
  beatName?: string;
  ghaName?: string;
  ghaEnvironment?: string;
  repoUrl?: string;
}

/** @public */
export interface TimeToMarketMetricScore {
  category: string;
  description: string;
  points: number;
}

/** @public */
export const METRIC_CATEGORIES = {
  ELITE: 'elite',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
} as const;

/** @public */
export interface TimeToMarketScoreResponse {
  timeToMarketScorePercent: number;
  deploymentFrequency: TimeToMarketMetricScore;
  leadTimeForChanges: TimeToMarketMetricScore;
  meanTimeToMerge: TimeToMarketMetricScore;
  meanWorkflowRunsSuccessRate: { successRate: string; points: number };
}

/** @public */
export interface DevOpsMetricsApi {
  getDevOpsMetrics(params: DevOpsMetricsQueryParams): Promise<DevOpsMetricsResponse>;
  getTimeToMarketScore(params: DevOpsMetricsQueryParams): Promise<TimeToMarketScoreResponse>;
}

/** @public */
export const devOpsMetricsApiRef = createApiRef<DevOpsMetricsApi>({
  id: 'plugin.devops-metrics.service',
});

/** @public */
export class DevOpsMetricsClient implements DevOpsMetricsApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  async getDevOpsMetrics(params: DevOpsMetricsQueryParams): Promise<DevOpsMetricsResponse> {
    // Validate that at least one of entityRef or repoUrl is provided
    if (!params.entityRef && !params.repoUrl) {
      throw new Error('Either entityRef or repoUrl must be provided');
    }

    const baseUrl = await this.discoveryApi.getBaseUrl('devops-metrics');
    const { token } = await this.identityApi.getCredentials();

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    const url = `${baseUrl}/metrics?${searchParams.toString()}`;

    let response: Response;
    try {
      response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
    } catch (networkError) {
      // Handle network-level errors: DNS failures, connection timeouts, network unavailability
      const errorMessage =
        networkError instanceof Error ? networkError.message : 'Unknown error';
      throw new Error(
        `Network error while fetching DevOps metrics: ${errorMessage}`,
      );
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch DevOps metrics: ${response.status} ${errorText}`,
      );
    }

    try {
      return await response.json();
    } catch (parseError) {
      throw new Error(
        `Failed to parse DevOps metrics response: ${
          parseError instanceof Error ? parseError.message : 'Invalid JSON'
        }`,
      );
    }
  }

  async getTimeToMarketScore(params: DevOpsMetricsQueryParams): Promise<TimeToMarketScoreResponse> {
    if (!params.entityRef && !params.repoUrl) {
      throw new Error('Either entityRef or repoUrl must be provided');
    }

    const baseUrl = await this.discoveryApi.getBaseUrl('devops-metrics');
    const { token } = await this.identityApi.getCredentials();

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    const url = `${baseUrl}/time-to-market-score?${searchParams.toString()}`;

    let response: Response;
    try {
      response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
    } catch (networkError) {
      const errorMessage =
        networkError instanceof Error ? networkError.message : 'Unknown error';
      throw new Error(
        `Network error while fetching Time to Market Score: ${errorMessage}`,
      );
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to fetch Time to Market Score: ${response.status} ${errorText}`,
      );
    }

    try {
      return await response.json();
    } catch (parseError) {
      throw new Error(
        `Failed to parse Time to Market Score response: ${parseError instanceof Error ? parseError.message : 'Invalid JSON'
        }`,
      );
    }
  }
}